import type { BatchScanOutcome, BatchScanRequest } from '@panelvault/shared';

/**
 * The phone's scan outbox. Every batch scan lands here first and is sent from
 * here, online or not: one path, so a scan made in a basement without signal
 * behaves exactly like one made on the sofa. Each scan carries an id made on
 * the device; the server answers a resent scan from its records instead of
 * adding a second copy.
 */

export interface OutboxEntry {
  clientScanId: string;
  code: string;
  addon: string | null;
  scannedAt: string;
  location: string | null;
  /** waiting: not answered yet (offline, or the server is still busy). */
  state: 'waiting' | 'done';
  outcome: BatchScanOutcome | null;
}

export interface OutboxStorage {
  load(): Promise<OutboxEntry[]>;
  save(entry: OutboxEntry): Promise<void>;
  remove(ids: string[]): Promise<void>;
}

export type SendBatch = (request: BatchScanRequest) => Promise<BatchScanOutcome[]>;

export interface SyncResult {
  sent: number;
  added: number;
  /** Why the sync stopped early, if it did. */
  stoppedBy: unknown;
}

/** The server takes at most this many scans per request. */
const BATCH_SIZE = 50;
/** Finished scans stay visible this long, then make room. */
const KEEP_DONE_MS = 24 * 60 * 60 * 1000;
const KEEP_DONE_MIN = 30;

/**
 * Version 4 UUID. `crypto.randomUUID` exists only in secure contexts, and the
 * app may run over plain HTTP on the LAN during setup.
 */
export function newScanId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function memoryStorage(initial: OutboxEntry[] = []): OutboxStorage {
  const entries = new Map(initial.map((entry) => [entry.clientScanId, entry]));
  return {
    load: async () => [...entries.values()],
    save: async (entry) => {
      entries.set(entry.clientScanId, entry);
    },
    remove: async (ids) => {
      for (const id of ids) entries.delete(id);
    },
  };
}

export class ScanOutbox {
  private entries: OutboxEntry[] = [];
  private listeners = new Set<() => void>();
  private syncListeners = new Set<(result: SyncResult) => void>();
  private syncing: Promise<SyncResult> | null = null;
  private ready: Promise<void> | null = null;

  constructor(
    private readonly storage: OutboxStorage,
    private readonly send: SendBatch,
    private readonly now: () => number = Date.now,
  ) {}

  /** Newest first, as the scanner lists them. */
  getSnapshot = (): readonly OutboxEntry[] => this.entries;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  onSynced(listener: (result: SyncResult) => void): () => void {
    this.syncListeners.add(listener);
    return () => this.syncListeners.delete(listener);
  }

  /** Loads what an earlier session left behind. Safe to call repeatedly. */
  init(): Promise<void> {
    this.ready ??= this.storage
      .load()
      .then((stored) => {
        const known = new Set(this.entries.map((entry) => entry.clientScanId));
        this.setEntries([...this.entries, ...stored.filter((e) => !known.has(e.clientScanId))]);
      })
      .catch(() => undefined);
    return this.ready;
  }

  get waitingCount(): number {
    return this.entries.filter((entry) => entry.state === 'waiting').length;
  }

  /** Same code (and add-on) captured within the window: a held-still camera. */
  isRecentDuplicate(code: string, addon: string | null, windowMs: number): boolean {
    const since = this.now() - windowMs;
    return this.entries.some(
      (entry) =>
        entry.code === code &&
        entry.addon === addon &&
        new Date(entry.scannedAt).getTime() >= since,
    );
  }

  async add(code: string, addon: string | null, location: string | null): Promise<OutboxEntry> {
    // Strictly increasing, so a fast stack keeps its order when sorted.
    const latest = this.entries[0] ? new Date(this.entries[0].scannedAt).getTime() : 0;
    const entry: OutboxEntry = {
      clientScanId: newScanId(),
      code,
      addon,
      scannedAt: new Date(Math.max(this.now(), latest + 1)).toISOString(),
      location,
      state: 'waiting',
      outcome: null,
    };
    this.setEntries([entry, ...this.entries]);
    await this.storage.save(entry).catch(() => undefined);
    return entry;
  }

  /**
   * Sends everything that is waiting, oldest first, grouped by location.
   * Only one sync runs at a time; a second call joins the running one.
   * Stops at the first failure (offline, logged out) and leaves the rest
   * waiting for the next attempt.
   */
  sync(): Promise<SyncResult> {
    this.syncing ??= this.runSync().finally(() => {
      this.syncing = null;
    });
    return this.syncing;
  }

  private async runSync(): Promise<SyncResult> {
    await this.init();
    const result: SyncResult = { sent: 0, added: 0, stoppedBy: null };

    const waiting = this.entries
      .filter((entry) => entry.state === 'waiting')
      .sort((a, b) => a.scannedAt.localeCompare(b.scannedAt));

    const byLocation = new Map<string | null, OutboxEntry[]>();
    for (const entry of waiting) {
      byLocation.set(entry.location, [...(byLocation.get(entry.location) ?? []), entry]);
    }

    outer: for (const [location, group] of byLocation) {
      for (let start = 0; start < group.length; start += BATCH_SIZE) {
        const chunk = group.slice(start, start + BATCH_SIZE);
        let outcomes: BatchScanOutcome[];
        try {
          outcomes = await this.send({
            location,
            scans: chunk.map(({ clientScanId, code, addon, scannedAt }) => ({
              clientScanId,
              code,
              addon,
              scannedAt,
            })),
          });
        } catch (error) {
          result.stoppedBy = error;
          break outer;
        }

        result.sent += chunk.length;
        for (const outcome of outcomes) {
          // "pending" means the server is still on it: ask again next time.
          if (outcome.status === 'pending') continue;
          if (outcome.status === 'added') result.added += 1;
          await this.update(outcome.clientScanId, { state: 'done', outcome });
        }
      }
    }

    await this.prune();
    for (const listener of this.syncListeners) listener(result);
    return result;
  }

  private async update(id: string, patch: Partial<OutboxEntry>): Promise<void> {
    let changed: OutboxEntry | null = null;
    this.setEntries(
      this.entries.map((entry) => {
        if (entry.clientScanId !== id) return entry;
        changed = { ...entry, ...patch };
        return changed;
      }),
    );
    if (changed) await this.storage.save(changed).catch(() => undefined);
  }

  /** Keeps the latest finished scans and drops old ones. */
  private async prune(): Promise<void> {
    const cutoff = this.now() - KEEP_DONE_MS;
    const done = this.entries.filter((entry) => entry.state === 'done');
    const drop = done
      .slice(KEEP_DONE_MIN)
      .filter((entry) => new Date(entry.scannedAt).getTime() < cutoff)
      .map((entry) => entry.clientScanId);
    if (drop.length === 0) return;

    const dropped = new Set(drop);
    this.setEntries(this.entries.filter((entry) => !dropped.has(entry.clientScanId)));
    await this.storage.remove(drop).catch(() => undefined);
  }

  private setEntries(entries: OutboxEntry[]): void {
    this.entries = [...entries].sort((a, b) => b.scannedAt.localeCompare(a.scannedAt));
    for (const listener of this.listeners) listener();
  }
}
